import { useEffect, useMemo, useState } from 'react';
import { Checkbox } from '@mui/material';

import { ErrorMessage } from '../../shared/ErrorMessage/ErrorMessage.tsx';
import { Section, SpeechBubble } from '../../shared';
import { UserRoles } from '@lifeserver/shared';

import { useUpdateCmsUserRoles } from '../../hooks/users.ts';

import type {
    CmsUserSummary,
    RoleCatalogBlock,
} from '../../types/userManagementTypes';

import styles from './RoleEditor.module.scss';

function roleSetKey(roles: string[]): string {
    return [...roles].sort().join(',');
}

const UNSAVED_CHANGES_KEY = 'hasUnsavedChanges';

const SELF_RESET_HINT =
    'You cannot reset your own roles; that would revoke your user-admin role.';

export function RoleEditor({
    user,
    blocks,
    isEditingSelf = false,
}: {
    user: CmsUserSummary;
    blocks: RoleCatalogBlock[];
    isEditingSelf?: boolean;
}) {
    const [draftRoles, setDraftRoles] = useState<string[]>(() => [
        ...user.roles,
    ]);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const { saveRoles, isSaving, errorMessage } = useUpdateCmsUserRoles();

    const isDirty = roleSetKey(user.roles) !== roleSetKey(draftRoles);

    const defaultRoles = useMemo(
        () =>
            blocks
                .flatMap((block) => block.roles)
                .filter((entry) => entry.isDefault)
                .map((entry) => entry.role),
        [blocks],
    );

    const resetWouldRevokeOwnAdmin =
        isEditingSelf && !defaultRoles.includes(UserRoles.UserAdmin);

    const canResetToDefaults =
        !resetWouldRevokeOwnAdmin &&
        !isSaving &&
        defaultRoles.length > 0 &&
        roleSetKey(draftRoles) !== roleSetKey(defaultRoles);

    useEffect(() => {
        localStorage.setItem(UNSAVED_CHANGES_KEY, String(isDirty));
    }, [isDirty]);

    useEffect(
        () => () => localStorage.setItem(UNSAVED_CHANGES_KEY, 'false'),
        [],
    );

    const toggleRole = (role: string, checked: boolean) => {
        setStatusMessage(null);
        setDraftRoles((previous) =>
            checked
                ? Array.from(new Set([...previous, role]))
                : previous.filter((value) => value !== role),
        );
    };

    const toggleBlock = (blockRoles: string[], checked: boolean) => {
        setStatusMessage(null);
        setDraftRoles((previous) =>
            checked
                ? Array.from(new Set([...previous, ...blockRoles]))
                : previous.filter((role) => !blockRoles.includes(role)),
        );
    };

    const onResetToDefaults = () => {
        setStatusMessage(null);
        setDraftRoles([...defaultRoles]);
    };

    const onSave = async () => {
        try {
            const saved = await saveRoles({
                userId: user.id,
                roles: draftRoles,
            });

            setStatusMessage(
                `Roles saved for ${saved.email}. They apply the next time the user signs in.`,
            );
        } catch {
            // The failure itself is surfaced through `errorMessage`; nothing
            // else to do here except keep the form editable.
        }
    };

    return (
        <>
            {blocks.map((block) => {
                const blockRoles = block.roles.map((entry) => entry.role);
                const checkedCount = blockRoles.filter((role) =>
                    draftRoles.includes(role),
                ).length;

                return (
                    <Section key={block.id} title={block.label}>
                        <label className={styles.selectAllRow}>
                            <Checkbox
                                size="small"
                                slotProps={{
                                    input: {
                                        'aria-label': `Select all ${block.label}`,
                                    },
                                }}
                                checked={
                                    checkedCount === blockRoles.length &&
                                    blockRoles.length > 0
                                }
                                indeterminate={
                                    checkedCount > 0 &&
                                    checkedCount < blockRoles.length
                                }
                                onChange={(event) =>
                                    toggleBlock(
                                        blockRoles,
                                        event.target.checked,
                                    )
                                }
                            />
                            <span className={styles.roleName}>Select all</span>
                        </label>

                        {block.roles.map((entry) => (
                            <label key={entry.role} className={styles.roleRow}>
                                <Checkbox
                                    size="small"
                                    checked={draftRoles.includes(entry.role)}
                                    onChange={(event) =>
                                        toggleRole(
                                            entry.role,
                                            event.target.checked,
                                        )
                                    }
                                />

                                <div className={styles.roleWrapper}>
                                    <span className={styles.roleName}>
                                        {entry.label}
                                    </span>
                                    <span className={styles.roleDescription}>
                                        {entry.description}
                                    </span>
                                    {entry.isDefault ? (
                                        <em className={styles.defaultTag}>
                                            (default)
                                        </em>
                                    ) : null}
                                </div>
                            </label>
                        ))}
                    </Section>
                );
            })}

            {errorMessage ? <ErrorMessage message={errorMessage} /> : null}

            <div className={styles.saveBar}>
                <div className={styles.resetGroup}>
                    <button
                        type="button"
                        className="unselected"
                        disabled={!canResetToDefaults}
                        onClick={onResetToDefaults}
                    >
                        Reset to defaults
                    </button>
                    {resetWouldRevokeOwnAdmin ? (
                        <span className={styles.resetHint}>
                            {SELF_RESET_HINT}
                        </span>
                    ) : null}
                </div>
                {statusMessage ? (
                    <SpeechBubble style={{ marginRight: 'auto' }}>
                        {statusMessage}
                    </SpeechBubble>
                ) : null}
                <button
                    type="button"
                    className="unselected"
                    disabled={!isDirty || isSaving}
                    onClick={() => setDraftRoles([...user.roles])}
                >
                    Discard changes
                </button>
                <button
                    type="button"
                    className="positive"
                    disabled={!isDirty || isSaving}
                    onClick={onSave}
                >
                    {isSaving ? 'Saving' : 'Save roles'}
                </button>
            </div>
        </>
    );
}
